import useSidebarState from '../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../utils/uiTranslation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import './Home.css';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, update } from 'firebase/database';
import { database } from '../firebase';
import FeatureGate from './Trial/FeatureGate';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

const COLORS = {
  blue: '#568DED',
  red: '#EB4040',
  yellow: '#FFD700',
  purple: '#CC40EB',
  green: '#40EB6E',
  background: 'var(--app-background, #031128)'
};

const NODE_SIZE = 112;
const ICON_SIZE = 23;
const GRAPH_SIZE = 680;

const defaultState = {
  mission: '',
  values: [{ name: '', description: '' }],
  culture: {
    communicationStyle: [],
    decisionMaking: [],
    riskTolerance: 'medium',
    pace: 'steady',
    workModes: { mode: 'hybrid', timezones: '', coreHours: '' },
    feedbackStyle: { frequency: '', format: '', examples: '' },
    writingStyle: { voice: [], length: 'concise', preferBullets: true },
    tools: '',
  },
  roleArchetypes: {
    Engineering: { traits: '', antiPatterns: '' },
    Product: { traits: '', antiPatterns: '' },
    Sales: { traits: '', antiPatterns: '' },
    Marketing: { traits: '', antiPatterns: '' },
    CS: { traits: '', antiPatterns: '' },
    Ops: { traits: '', antiPatterns: '' },
  },
  hiring: {
    mustHaves: '',
    niceToHaves: '',
    interviewSignals: '',
    dealBreakers: ''
  },
  success: {
    companyWide: '',
    byFunction: ''
  },
  teamNorms: {
    meetings: '',
    documentation: '',
    onboarding3090: ''
  },
  candidateFit: {
    personality: '',
    workstyle: '',
    motivators: '',
    demotivators: ''
  },
  hiringPatterns: {
    entries: []
  },
  redFlags: '',
  diversity: '',
  security: ''
};

const COMMUNICATION_OPTIONS = ['direct', 'candid', 'formal', 'casual', 'async-first', 'meeting-light', 'document-first'];
const DECISION_OPTIONS = ['data-driven', 'experiment-driven', 'consensus', 'top-down', 'bottom-up', 'fast-bias', 'rigorous-review'];
const RISK_OPTIONS = ['low', 'medium', 'high'];
const PACE_OPTIONS = ['calm', 'steady', 'fast', 'hypergrowth'];
const WORK_MODE_OPTIONS = ['remote', 'hybrid', 'onsite'];
const WRITING_VOICE_OPTIONS = ['authoritative', 'friendly', 'humble', 'enthusiastic'];
const WRITING_LENGTH_OPTIONS = ['concise', 'detailed'];
const ROLE_KEYS = Object.keys(defaultState.roleArchetypes);

const nodeConfig = [
  {
    key: 'hiringPatterns',
    title: 'Hiring\nPatterns',
    drawerTitle: 'Hiring Patterns',
    icon: 'fa-solid fa-handshake-angle',
    color: COLORS.green,
    x: 22,
    y: 22
  },
  {
    key: 'roleArchetypes',
    title: 'Role\nArchetypes',
    drawerTitle: 'Role Archetypes',
    icon: 'fa-solid fa-trophy',
    color: COLORS.yellow,
    x: 78,
    y: 22
  },
  {
    key: 'redFlags',
    title: 'Red Flags &\nGuardrails',
    drawerTitle: 'Red Flags & Guardrails',
    icon: 'fa-solid fa-flag',
    color: COLORS.red,
    x: 22,
    y: 78
  },
  {
    key: 'culture',
    title: 'Culture &\nCore Values',
    drawerTitle: 'Culture & Core Values',
    icon: 'fa-solid fa-gem',
    color: COLORS.purple,
    x: 78,
    y: 78
  }
];

const centerNode = {
  title: 'AI Agent',
  icon: 'fa-solid fa-brain',
  color: COLORS.blue,
  x: 50,
  y: 50
};

const fieldStyle = {
  width: '100%',
  borderRadius: 10,
  border: `1px solid ${COLORS.blue}`,
  background: 'var(--app-surface, rgba(255,255,255,0.03))',
  color: 'var(--app-text, #FFFFFF)',
  padding: '12px 14px',
  fontFamily: 'Montserrat',
  fontSize: 15,
  outline: 'none',
  boxSizing: 'border-box',
  WebkitTextFillColor: 'var(--app-text, #FFFFFF)',
  caretColor: 'var(--app-text, #FFFFFF)',
  transition: 'box-shadow 0.18s'
};

const sectionTitleStyle = {
  color: 'var(--app-text, #FFFFFF)',
  fontFamily: 'Montserrat',
  fontSize: 18,
  fontWeight: 500,
  margin: '0 0 12px'
};

const toRgba = (hex, opacity) => {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const getLineEndpoints = (node) => {
  const center = { x: 50, y: 50 };
  const end = { x: node.x, y: node.y };

  return {
    x1: center.x,
    y1: center.y,
    x2: end.x,
    y2: end.y
  };
};

const headerHelp = {
  mission: 'Write the short company purpose and operating context the AI should preserve when assessing role fit.',
  values: 'Add the values and short behavioral notes that should show up consistently in hiring recommendations.',
  communicationDecision: 'Choose how the company communicates and makes decisions so the AI can distinguish strong fit from style mismatch.',
  operatingNorms: 'Capture pace, risk tolerance, work mode, and timezone expectations that affect day-to-day success.',
  feedbackWriting: 'Describe how your team gives feedback, writes, documents work, and onboards people.',
  candidateFitSuccess: 'Define what strong candidate fit and successful performance look like across personality, work style, and outcomes.',
  roleArchetypes: 'Describe what high performers and weak fits look like by function so the AI can calibrate role-specific recommendations.',
  hiringGuardrails: 'List must-haves, nice-to-haves, interview signals, and deal-breakers that should guide hiring decisions.',
  redFlagsCompliance: 'Document risk signals, inclusion requirements, and security or compliance boundaries the AI should respect.'
};

const editableNodeKeys = new Set(['culture', 'roleArchetypes', 'redFlags']);
const unsavedChangesMessage = 'You have unsaved org intelligence changes. If you leave this page, your changes will be lost.';
const SENIORITY_OPTIONS = ['Junior Hires', 'Mid-Level Hires', 'Senior Hires', 'Leadership Hires'];
const CATEGORY_OPTIONS = ['Engineering', 'Product', 'Sales', 'Marketing', 'Customer Success', 'Operations'];

const HeaderTooltip = ({ text }) => (
  <span className="org-intelligence-info-tooltip" tabIndex={0} aria-label={text}>
    <span className="org-intelligence-info-icon">i</span>
    <span className="org-intelligence-info-bubble">{text}</span>
  </span>
);

const DrawerSection = ({ title, tooltip, children }) => (
  <section style={{ marginTop: 28 }}>
    <h3 style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>{title}</span>
      {tooltip ? <HeaderTooltip text={tooltip} /> : null}
    </h3>
    {children}
  </section>
);

const DrawerTextarea = ({ value, onChange, placeholder, minHeight = 72, isRTL }) => (
  <textarea
    className="org-intelligence-field"
    value={value || ''}
    onChange={onChange}
    placeholder={placeholder}
    style={{ ...fieldStyle, minHeight, resize: 'vertical', direction: isRTL ? 'rtl' : 'ltr' }}
  />
);

const DrawerInput = ({ value, onChange, placeholder, isRTL }) => (
  <input
    className="org-intelligence-field"
    type="text"
    value={value || ''}
    onChange={onChange}
    placeholder={placeholder}
    style={{ ...fieldStyle, direction: isRTL ? 'rtl' : 'ltr' }}
  />
);

const OptionButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      border: `1px solid ${active ? COLORS.blue : 'var(--app-border, rgba(255,255,255,0.24))'}`,
      background: active ? 'var(--app-surface, rgba(86,141,237,0.20))' : 'var(--app-surface, rgba(255,255,255,0.04))',
      color: 'var(--app-text, #FFFFFF)',
      borderRadius: 999,
      padding: '8px 12px',
      fontFamily: 'Montserrat',
      fontSize: 13,
      cursor: 'pointer'
    }}
    data-light-tab={active}
              data-light-control={active ? "quiet" : "quiet"}>
    {children}
  </button>
);

const FilterSelect = ({ value, onChange, children }) => (
  <select
    className="org-intelligence-select"
    value={value}
    onChange={onChange}
    style={{
      height: 40,
      minWidth: 170,
      borderRadius: 999,
      border: `1px solid ${COLORS.blue}`,
      background: 'var(--app-surface, rgba(86,141,237,0.10))',
      color: 'var(--app-text, #FFFFFF)',
      fontFamily: 'Montserrat',
      fontSize: 14,
      padding: '0 16px',
      outline: 'none',
      WebkitTextFillColor: 'var(--app-text, #FFFFFF)',
      caretColor: 'var(--app-text, #FFFFFF)',
      appearance: 'none',
      WebkitAppearance: 'none',
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'%3E%3Cpath d='M3 5.25L7 9.25L11 5.25' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 14px center',
      backgroundSize: '14px 14px'
    }}
  >
    {children}
  </select>
);

const OrgNode = ({ node, active, onClick, center = false, isRTL = false, t }) => (
  <div
    style={{
      position: 'absolute',
      left: `${node.x}%`,
      top: `${node.y}%`,
      transform: 'translate(-50%, -50%)',
      width: NODE_SIZE,
      height: NODE_SIZE,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}
  >
    <button
      type="button"
      onClick={center ? undefined : onClick}
      aria-label={node.drawerTitle || node.title}
      style={{
        width: NODE_SIZE,
        height: NODE_SIZE,
        borderRadius: '50%',
        border: `1.33px solid ${node.color}`,
        background: `linear-gradient(${toRgba(node.color, active ? 0.3 : 0.1)}, ${toRgba(node.color, active ? 0.3 : 0.1)}), ${COLORS.background}`,
        color: 'var(--app-text, #FFFFFF)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: 0,
        cursor: center ? 'default' : 'pointer',
        fontFamily: 'Montserrat',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '18px',
        textAlign: 'center',
        whiteSpace: 'pre-line',
        boxSizing: 'border-box'
      }}
    >
      <i
        className={node.icon}
        style={{
          color: node.color,
          fontSize: ICON_SIZE,
          width: ICON_SIZE,
          height: ICON_SIZE,
          lineHeight: `${ICON_SIZE}px`,
          marginBottom: 7
        }}
      ></i>
      <span>{uiText(node.title)}</span>
    </button>
    {!center ? (
      <button
        type="button"
        className="org-intelligence-configure-button"
        onClick={onClick}
        style={{
          '--node-color': node.color,
          '--node-soft-color': toRgba(node.color, 0.12),
          color: node.color
        }}
      >
        <span>{t('actions.configure')}</span>
        <i className={`fa-solid ${isRTL ? 'fa-arrow-left' : 'fa-arrow-right'}`} aria-hidden="true"></i>
      </button>
    ) : null}
  </div>
);

const PageHeader = ({ t }) => (
  <header
    className="org-intelligence-page-header"
    style={{
      position: 'absolute',
      top: 42,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 4,
      width: 'min(820px, calc(100% - 64px))',
      textAlign: 'center',
      color: 'var(--app-text, #FFFFFF)',
      fontFamily: 'Montserrat',
      pointerEvents: 'none'
    }}
  >
    <h1 style={{ margin: 0, fontFamily: 'Montserrat, sans-serif', fontSize: 30, lineHeight: '36px', fontWeight: "var(--app-weight-strong, 900)" }}>
      {t('pageTitle')}
    </h1>
    <p style={{ margin: '10px auto 0', maxWidth: 760, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.72)', fontSize: 15, lineHeight: '24px', fontWeight: 400 }}>
      {t('pageSubtitle')}
    </p>
  </header>
);

const GraphLines = () => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
    <defs>
      {nodeConfig.map((node) => (
        <linearGradient key={node.key} id={`line-${node.key}`} x1="50%" y1="50%" x2={`${node.x}%`} y2={`${node.y}%`} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={COLORS.blue} stopOpacity="0.1" />
          <stop offset="100%" stopColor={node.color} stopOpacity="0.3" />
        </linearGradient>
      ))}
    </defs>
    {nodeConfig.map((node) => (
      <line
        key={node.key}
        {...getLineEndpoints(node)}
        stroke={`url(#line-${node.key})`}
        strokeWidth="4"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
      />
    ))}
  </svg>
);

const OrgGraph = ({ activeNode, onSelect, isRTL, t }) => (
  <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: COLORS.background }}>
    <PageHeader t={t} />
    <div
      style={{
        position: 'absolute',
        width: GRAPH_SIZE,
        height: GRAPH_SIZE,
        left: activeNode ? '49%' : '50%',
        top: '50%',
        transform: `translate(-50%, -50%) scale(${activeNode ? 0.92 : 1})`,
        transformOrigin: 'center',
        transition: 'left 240ms ease, transform 240ms ease'
      }}
    >
      <GraphLines />
      <OrgNode node={centerNode} active={false} center />
      {nodeConfig.map((node) => (
        <OrgNode key={node.key} node={node} active={activeNode === node.key} onClick={() => onSelect(node.key)} isRTL={isRTL} t={t} />
      ))}
    </div>
  </div>
);

const OrgIntelligence = () => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const { t } = useTranslation('orgIntelligence');
  const { isRTL, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [companyUID, setCompanyUID] = useState('');
  const [data, setData] = useState(defaultState);
  const [saving, setSaving] = useState({});
  const [savedAtBySection, setSavedAtBySection] = useState({});
  const [activeNode, setActiveNode] = useState(null);
  const [dirtyBySection, setDirtyBySection] = useState({});
  const [patternSeniority, setPatternSeniority] = useState('all');
  const [patternCategory, setPatternCategory] = useState('all');
  const [patternKeyword, setPatternKeyword] = useState('all');
  const dirtyRef = useRef(false);
  const navigationBypassRef = useRef(false);
  const currentPathRef = useRef(`${location.pathname}${location.search}${location.hash}`);

  useEffect(() => {
    const init = async () => {
      if (!currentUser) return;
      try {
        let uid = currentUser.uid;
        const ownerSnap = await get(ref(database, `companies/${uid}`));
        if (!ownerSnap.exists()) {
          const collabSnap = await get(ref(database, `collaborators/${uid}`));
          if (collabSnap.exists()) uid = collabSnap.val().companyId;
        }
        setCompanyUID(uid);
        const snap = await get(ref(database, `companies/${uid}/orgIntelligence`));
        if (snap.exists()) {
          const v = snap.val();
          setData(prev => ({ ...prev, ...v }));
        }
      } catch (e) {
        console.error('Load org intelligence failed:', e);
      }
    };
    init();
  }, [currentUser]);

  useEffect(() => {
    dirtyRef.current = Object.values(dirtyBySection).some(Boolean);
  }, [dirtyBySection]);

  useEffect(() => {
    currentPathRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!dirtyRef.current || navigationBypassRef.current) return;
      event.preventDefault();
      event.returnValue = unsavedChangesMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!dirtyRef.current || navigationBypassRef.current || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target || anchor.hasAttribute('download')) return;

      const nextUrl = new URL(anchor.href, window.location.origin);
      const currentUrl = new URL(window.location.href);
      if (nextUrl.href === currentUrl.href) return;

      event.preventDefault();
      if (!window.confirm(unsavedChangesMessage)) return;

      navigationBypassRef.current = true;
      if (nextUrl.origin === window.location.origin) {
        navigate(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      } else {
        window.location.href = nextUrl.href;
      }
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [navigate]);

  useEffect(() => {
    const handlePopState = () => {
      if (!dirtyRef.current || navigationBypassRef.current) return;
      const stayPath = currentPathRef.current;
      if (window.confirm(unsavedChangesMessage)) {
        navigationBypassRef.current = true;
        return;
      }

      setTimeout(() => navigate(stayPath, { replace: true }), 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  const markActiveNodeDirty = useCallback(() => {
    if (!editableNodeKeys.has(activeNode)) return;
    setDirtyBySection(prev => ({ ...prev, [activeNode]: true }));
  }, [activeNode]);

  const setField = (path, value) => {
    setData(prev => {
      const next = { ...prev };
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        cur[p] = Array.isArray(cur[p]) ? [...cur[p]] : { ...(cur[p] || {}) };
        cur = cur[p];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
    markActiveNodeDirty();
  };

  const toggleInArray = (arr, value) => (
    arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
  );

  const addValueRow = () => {
    setData(d => ({ ...d, values: [...(d.values || []), { name: '', description: '' }] }));
    markActiveNodeDirty();
  };

  const removeValueRow = (idx) => {
    setData(d => ({ ...d, values: (d.values || []).filter((_, i) => i !== idx) }));
    markActiveNodeDirty();
  };

  const completion = useMemo(() => {
    const vals = (data.values || []).filter(v => (v.name || '').trim() && (v.description || '').trim());
    const roleCount = Object.values(data.roleArchetypes || {}).filter(x => ((x?.traits || '').trim().length >= 30 && (x?.antiPatterns || '').trim().length >= 30)).length;
    return {
      mission: (data.mission || '').trim().length >= 30,
      values: vals.length >= 4,
      culture: !!((data.culture?.communicationStyle || []).length && (data.culture?.decisionMaking || []).length),
      roleArchetypes: roleCount >= 3,
      hiring: !!((data.hiring?.mustHaves || '').trim() && (data.hiring?.dealBreakers || '').trim()),
      success: !!((data.success?.companyWide || '').trim() && (data.success?.byFunction || '').trim()),
      teamNorms: !!((data.teamNorms?.meetings || '').trim() && (data.teamNorms?.documentation || '').trim()),
      candidateFit: !!((data.candidateFit?.personality || '').trim() && (data.candidateFit?.workstyle || '').trim()),
      safety: !!((data.redFlags || '').trim() && (data.diversity || '').trim() && (data.security || '').trim())
    };
  }, [data]);

  const getUpdatePayloadForSection = useCallback((key) => {
    switch (key) {
      case 'culture':
        return {
          mission: data.mission,
          values: data.values,
          culture: data.culture,
          teamNorms: data.teamNorms,
          candidateFit: data.candidateFit,
          success: data.success
        };
      case 'roleArchetypes':
        return { roleArchetypes: data.roleArchetypes };
      case 'redFlags':
        return {
          hiring: data.hiring,
          redFlags: data.redFlags,
          diversity: data.diversity,
          security: data.security
        };
      default:
        return {};
    }
  }, [data]);

  const saveSection = useCallback(async (key) => {
    if (!companyUID || !editableNodeKeys.has(key)) return false;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const now = Date.now();
      const partial = getUpdatePayloadForSection(key);
      await update(ref(database, `companies/${companyUID}/orgIntelligence`), { ...partial, lastSavedAt: now, completion });
      setSavedAtBySection(s => ({ ...s, [key]: now }));
      setDirtyBySection(s => ({ ...s, [key]: false }));
      return true;
    } catch (e) {
      console.error('Save failed:', e);
      return false;
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  }, [companyUID, completion, getUpdatePayloadForSection]);

  const saveActiveNode = useCallback(async () => {
    if (!editableNodeKeys.has(activeNode)) return false;
    return saveSection(activeNode);
  }, [activeNode, saveSection]);

  const handleSelectNode = useCallback((nextNode) => {
    if (nextNode === activeNode) return;
    const previousNode = activeNode;
    setActiveNode(nextNode);
    if (editableNodeKeys.has(previousNode) && dirtyBySection[previousNode]) {
      saveSection(previousNode);
    }
  }, [activeNode, dirtyBySection, saveSection]);

  const sidebarWidth = sidebarCollapsed ? '60px' : '240px';
  const activeNodeConfig = nodeConfig.find(node => node.key === activeNode);
  const formatSavedTime = (timestamp) => new Date(timestamp).toLocaleTimeString(language === 'ar' ? 'ar-EG' : undefined, { hour: 'numeric', minute: '2-digit' });
  const hiringPatternEntries = Array.isArray(data.hiringPatterns?.entries)
    ? data.hiringPatterns.entries
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry, index) => ({
          id: entry.id || `pattern-${index}`,
          title: entry.title || '',
          body: entry.body || '',
          seniority: entry.seniority || '',
          category: entry.category || '',
          keywords: Array.isArray(entry.keywords) ? entry.keywords.filter(Boolean) : []
        }))
    : [];
  const hiringPatternKeywords = Array.from(new Set(hiringPatternEntries.flatMap((entry) => entry.keywords))).sort((a, b) => a.localeCompare(b));
  const filteredHiringPatternEntries = hiringPatternEntries.filter((entry) => {
    if (patternSeniority !== 'all' && entry.seniority !== patternSeniority) return false;
    if (patternCategory !== 'all' && entry.category !== patternCategory) return false;
    if (patternKeyword !== 'all' && !entry.keywords.includes(patternKeyword)) return false;
    return true;
  });

  const renderCultureDrawer = () => (
    <>
      <DrawerSection title={uiText("Mission")} tooltip={headerHelp.mission}>
        <DrawerTextarea
          value={data.mission}
          onChange={e => setField('mission', e.target.value)}
          placeholder={t('sections.mission.placeholder')}
          minHeight={88}
          isRTL={isRTL}
        />
      </DrawerSection>

      <DrawerSection title={uiText("Core Values")} tooltip={headerHelp.values}>
        {(data.values || []).map((v, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) minmax(160px, 2fr) 42px', gap: 8, marginBottom: 10 }}>
            <DrawerInput
              value={v.name}
              onChange={e => {
                const next = [...(data.values || [])];
                next[idx] = { ...next[idx], name: e.target.value };
                setField('values', next);
              }}
              placeholder={t('sections.values.namePlaceholder')}
              isRTL={isRTL}
            />
            <DrawerInput
              value={v.description}
              onChange={e => {
                const next = [...(data.values || [])];
                next[idx] = { ...next[idx], description: e.target.value };
                setField('values', next);
              }}
              placeholder={t('sections.values.notePlaceholder')}
              isRTL={isRTL}
            />
            <button className="post-job-cta" onClick={() => removeValueRow(idx)} title={t('common:buttons.remove')} aria-label={t('common:buttons.remove')} style={{ width: 42, minWidth: 42, padding: 0 }}>
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        ))}
        <button className="post-job-cta" onClick={addValueRow}>
          <i className="fa-solid fa-circle-plus"></i>
          <span>{t('sections.values.add')}</span>
        </button>
      </DrawerSection>

      <DrawerSection title={uiText("Communication & Decision Style")} tooltip={headerHelp.communicationDecision}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {COMMUNICATION_OPTIONS.map(k => (
            <OptionButton key={k} active={(data.culture.communicationStyle || []).includes(k)} onClick={() => setField('culture.communicationStyle', toggleInArray(data.culture.communicationStyle || [], k))}>
              {t(`sections.culture.options.communicationStyle.${k}`)}
            </OptionButton>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {DECISION_OPTIONS.map(k => (
            <OptionButton key={k} active={(data.culture.decisionMaking || []).includes(k)} onClick={() => setField('culture.decisionMaking', toggleInArray(data.culture.decisionMaking || [], k))}>
              {t(`sections.culture.options.decisionMaking.${k}`)}
            </OptionButton>
          ))}
        </div>
      </DrawerSection>

      <DrawerSection title={uiText("Operating Norms")} tooltip={headerHelp.operatingNorms}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {RISK_OPTIONS.map(k => (
            <OptionButton key={k} active={data.culture.riskTolerance === k} onClick={() => setField('culture.riskTolerance', k)}>
              {t(`sections.culture.options.riskTolerance.${k}`)}
            </OptionButton>
          ))}
          {PACE_OPTIONS.map(k => (
            <OptionButton key={k} active={data.culture.pace === k} onClick={() => setField('culture.pace', k)}>
              {t(`sections.culture.options.pace.${k}`)}
            </OptionButton>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {WORK_MODE_OPTIONS.map(k => (
            <OptionButton key={k} active={(data.culture.workModes?.mode || '') === k} onClick={() => setField('culture.workModes', { ...(data.culture.workModes || {}), mode: k })}>
              {t(`sections.culture.options.workMode.${k}`)}
            </OptionButton>
          ))}
        </div>
        <DrawerInput value={data.culture.workModes?.timezones || ''} onChange={e => setField('culture.workModes', { ...(data.culture.workModes || {}), timezones: e.target.value })} placeholder={t('sections.culture.timezonePlaceholder')} isRTL={isRTL} />
      </DrawerSection>

      <DrawerSection title={uiText("Feedback, Writing & Team Norms")} tooltip={headerHelp.feedbackWriting}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <DrawerInput value={data.culture.feedbackStyle?.frequency || ''} onChange={e => setField('culture.feedbackStyle', { ...(data.culture.feedbackStyle || {}), frequency: e.target.value })} placeholder={t('sections.culture.feedbackFrequency')} isRTL={isRTL} />
          <DrawerInput value={data.culture.feedbackStyle?.format || ''} onChange={e => setField('culture.feedbackStyle', { ...(data.culture.feedbackStyle || {}), format: e.target.value })} placeholder={t('sections.culture.feedbackFormat')} isRTL={isRTL} />
        </div>
        <DrawerInput value={data.culture.feedbackStyle?.examples || ''} onChange={e => setField('culture.feedbackStyle', { ...(data.culture.feedbackStyle || {}), examples: e.target.value })} placeholder={t('sections.culture.feedbackExamples')} isRTL={isRTL} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {WRITING_VOICE_OPTIONS.map(k => (
            <OptionButton key={k} active={(data.culture.writingStyle?.voice || []).includes(k)} onClick={() => setField('culture.writingStyle', { ...(data.culture.writingStyle || {}), voice: toggleInArray(data.culture.writingStyle?.voice || [], k) })}>
              {t(`sections.culture.options.writingVoice.${k}`)}
            </OptionButton>
          ))}
          {WRITING_LENGTH_OPTIONS.map(k => (
            <OptionButton key={k} active={data.culture.writingStyle?.length === k} onClick={() => setField('culture.writingStyle', { ...(data.culture.writingStyle || {}), length: k })}>
              {t(`sections.culture.options.writingLength.${k}`)}
            </OptionButton>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 12 }}>
          <DrawerTextarea value={data.teamNorms.meetings} onChange={e => setField('teamNorms.meetings', e.target.value)} placeholder={t('sections.teamNorms.meetings')} isRTL={isRTL} />
          <DrawerTextarea value={data.teamNorms.documentation} onChange={e => setField('teamNorms.documentation', e.target.value)} placeholder={t('sections.teamNorms.documentation')} isRTL={isRTL} />
          <DrawerTextarea value={data.teamNorms.onboarding3090} onChange={e => setField('teamNorms.onboarding3090', e.target.value)} placeholder={t('sections.teamNorms.onboarding3090')} isRTL={isRTL} />
        </div>
      </DrawerSection>

      <DrawerSection title={uiText("Candidate Fit & Success")} tooltip={headerHelp.candidateFitSuccess}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <DrawerTextarea value={data.candidateFit.personality} onChange={e => setField('candidateFit.personality', e.target.value)} placeholder={t('sections.candidateFit.personality')} isRTL={isRTL} />
          <DrawerTextarea value={data.candidateFit.workstyle} onChange={e => setField('candidateFit.workstyle', e.target.value)} placeholder={t('sections.candidateFit.workstyle')} isRTL={isRTL} />
          <DrawerTextarea value={data.candidateFit.motivators} onChange={e => setField('candidateFit.motivators', e.target.value)} placeholder={t('sections.candidateFit.motivators')} isRTL={isRTL} />
          <DrawerTextarea value={data.candidateFit.demotivators} onChange={e => setField('candidateFit.demotivators', e.target.value)} placeholder={t('sections.candidateFit.demotivators')} isRTL={isRTL} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 10 }}>
          <DrawerTextarea value={data.success.companyWide} onChange={e => setField('success.companyWide', e.target.value)} placeholder={t('sections.success.companyWide')} isRTL={isRTL} />
          <DrawerTextarea value={data.success.byFunction} onChange={e => setField('success.byFunction', e.target.value)} placeholder={t('sections.success.byFunction')} isRTL={isRTL} />
        </div>
      </DrawerSection>

    </>
  );

  const renderRoleDrawer = () => (
    <>
      <DrawerSection title={uiText("Role Archetype Inputs")} tooltip={headerHelp.roleArchetypes}>
        {ROLE_KEYS.map((roleKey) => (
          <div key={roleKey} style={{ marginBottom: 18 }}>
            <h4 style={{ color: 'var(--app-text, #FFFFFF)', fontFamily: 'Montserrat', fontSize: 16, fontWeight: 500, margin: '0 0 8px' }}>
              {t(`sections.roleArchetypes.roleNames.${roleKey}`)}
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
              <DrawerTextarea value={data.roleArchetypes?.[roleKey]?.traits || ''} onChange={e => setField(`roleArchetypes.${roleKey}.traits`, e.target.value)} placeholder={t('sections.roleArchetypes.traitsPlaceholder')} isRTL={isRTL} />
              <DrawerTextarea value={data.roleArchetypes?.[roleKey]?.antiPatterns || ''} onChange={e => setField(`roleArchetypes.${roleKey}.antiPatterns`, e.target.value)} placeholder={t('sections.roleArchetypes.antiPatternsPlaceholder')} isRTL={isRTL} />
            </div>
          </div>
        ))}
      </DrawerSection>
    </>
  );

  const renderRedFlagsDrawer = () => (
    <>
      <DrawerSection title={uiText("Hiring Guardrails")} tooltip={headerHelp.hiringGuardrails}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <DrawerTextarea value={data.hiring.mustHaves} onChange={e => setField('hiring.mustHaves', e.target.value)} placeholder={t('sections.hiring.mustHaves')} isRTL={isRTL} />
          <DrawerTextarea value={data.hiring.niceToHaves} onChange={e => setField('hiring.niceToHaves', e.target.value)} placeholder={t('sections.hiring.niceToHaves')} isRTL={isRTL} />
          <DrawerTextarea value={data.hiring.interviewSignals} onChange={e => setField('hiring.interviewSignals', e.target.value)} placeholder={t('sections.hiring.interviewSignals')} isRTL={isRTL} />
          <DrawerTextarea value={data.hiring.dealBreakers} onChange={e => setField('hiring.dealBreakers', e.target.value)} placeholder={t('sections.hiring.dealBreakers')} isRTL={isRTL} />
        </div>
      </DrawerSection>

      <DrawerSection title={uiText("Red Flags & Compliance")} tooltip={headerHelp.redFlagsCompliance}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <DrawerTextarea value={data.redFlags} onChange={e => setField('redFlags', e.target.value)} placeholder={t('sections.safety.redFlags')} isRTL={isRTL} />
          <DrawerTextarea value={data.diversity} onChange={e => setField('diversity', e.target.value)} placeholder={t('sections.safety.diversity')} isRTL={isRTL} />
          <DrawerTextarea value={data.security} onChange={e => setField('security', e.target.value)} placeholder={t('sections.safety.security')} isRTL={isRTL} />
        </div>
      </DrawerSection>
    </>
  );

  const renderHiringPatternsDrawer = () => {
    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 24, marginBottom: 34 }}>
          <FilterSelect value={patternSeniority} onChange={e => setPatternSeniority(e.target.value)}>
            <option value="all">{uiText("Filter: All Seniority")}</option>
            {SENIORITY_OPTIONS.map((option) => (
              <option key={option} value={option}>{uiText(option)}</option>
            ))}
          </FilterSelect>
          <FilterSelect value={patternCategory} onChange={e => setPatternCategory(e.target.value)}>
            <option value="all">{uiText("Filter: All Categories")}</option>
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option} value={option}>{uiText(option)}</option>
            ))}
          </FilterSelect>
          <FilterSelect value={patternKeyword} onChange={e => setPatternKeyword(e.target.value)}>
            <option value="all">{uiText("Filter: All Keywords")}</option>
            {hiringPatternKeywords.map((keyword) => (
              <option key={keyword} value={keyword}>{keyword}</option>
            ))}
          </FilterSelect>
        </div>

        {filteredHiringPatternEntries.length ? (
          <div>
            {filteredHiringPatternEntries.map((entry) => (
              <div key={entry.id} style={{ display: 'flex', alignItems: 'stretch', marginBottom: 35 }}>
                <div style={{ width: 8, display: 'flex', borderRadius: 5, overflow: 'hidden', flex: '0 0 8px' }}>
                  <div style={{ width: 5, background: COLORS.green }}></div>
                  <div style={{ width: 3, background: toRgba(COLORS.green, 0.3) }}></div>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h3 style={{ margin: 0, color: 'var(--app-text, #FFFFFF)', fontFamily: 'Montserrat', fontWeight: 500, fontSize: 20, lineHeight: '24px' }}>{entry.title}</h3>
                  <p style={{ margin: '5px 0 0', color: 'var(--app-text, #FFFFFF)', fontFamily: 'Montserrat', fontWeight: 400, fontSize: 16, lineHeight: '24px' }}>{entry.body}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--app-text, #FFFFFF)', fontFamily: 'Montserrat', fontSize: 16, lineHeight: '24px' }}>{uiText("This feed populates as you hire more via JOBSEEQR.")}</p>
        )}
      </>
    );
  };

  const renderDrawerContent = () => {
    if (activeNode === 'hiringPatterns') return renderHiringPatternsDrawer();
    if (activeNode === 'roleArchetypes') return renderRoleDrawer();
    if (activeNode === 'redFlags') return renderRedFlagsDrawer();
    if (activeNode === 'culture') return renderCultureDrawer();
    return null;
  };

  return (
    <div style={{ height: '100vh' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div
        className={`home-body ${isRTL ? 'rtl' : 'ltr'}`}
        style={{
          marginLeft: isRTL ? 'auto' : sidebarWidth,
          marginRight: isRTL ? sidebarWidth : 'auto',
          transition: 'all 250ms ease',
          width: `calc(100vw - ${sidebarWidth})`,
          height: '100vh',
          padding: 0,
          overflow: 'hidden',
          direction: isRTL ? 'rtl' : 'ltr',
          background: COLORS.background
        }}
      >
        <style>
          {`
            .org-intelligence-field::placeholder {
              color: rgba(var(--app-ink-rgb, 255, 255, 255), 0.36);
              -webkit-text-fill-color: rgba(var(--app-ink-rgb, 255, 255, 255), 0.36);
            }

            .org-intelligence-field:focus,
            .org-intelligence-select:focus {
              box-shadow: 0 0 0 3px rgba(86, 141, 237, 0.18);
            }

            .org-intelligence-select option {
              color: var(--app-heading, #031128);
              background: #FFFFFF;
            }

            .org-intelligence-info-tooltip {
              position: relative;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 18px;
              height: 18px;
              flex: 0 0 18px;
              outline: none;
            }

            .org-intelligence-info-icon {
              width: 18px;
              height: 18px;
              border-radius: 50%;
              border: 1px solid rgba(86, 141, 237, 0.78);
              color: var(--app-text, #FFFFFF);
              background: var(--app-surface, rgba(86, 141, 237, 0.16));
              display: inline-flex;
              align-items: center;
              justify-content: center;
              font-family: Montserrat, sans-serif;
              font-size: 11px;
              line-height: 18px;
              font-weight: var(--app-weight-strong, 700);
              font-style: normal;
              cursor: help;
              box-sizing: border-box;
            }

            .org-intelligence-info-bubble {
              position: absolute;
              bottom: calc(100% + 10px);
              inset-inline-start: 50%;
              transform: translateX(-50%);
              width: 280px;
              background-color: var(--app-surface, #0f2349);
              border: 1px solid var(--app-border, rgba(255, 255, 255, 0.12));
              border-radius: 10px;
              padding: 10px 12px;
              color: var(--app-text, #FFFFFF);
              font-family: Montserrat, sans-serif;
              font-size: 12px;
              line-height: 1.5;
              font-weight: 500;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.35);
              opacity: 0;
              pointer-events: none;
              transition: opacity 0.2s ease;
              z-index: 30;
            }

            .org-intelligence-info-tooltip:hover .org-intelligence-info-bubble,
            .org-intelligence-info-tooltip:focus-visible .org-intelligence-info-bubble {
              opacity: 1;
            }

            .org-intelligence-close {
              width: 36px;
              height: 36px;
              min-width: 36px;
              padding: 0;
              border: none;
              border-radius: 50%;
              background: transparent;
              color: rgba(var(--app-ink-rgb, 255, 255, 255), 0.74);
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
              line-height: 1;
              transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
            }

            .org-intelligence-close i {
              font-size: 18px;
              line-height: 1;
            }

            .org-intelligence-close:hover,
            .org-intelligence-close:focus-visible {
              background: var(--app-surface-hover, rgba(86, 141, 237, 0.16));
              color: var(--app-text, #FFFFFF);
              outline: none;
            }

            .org-intelligence-close:active {
              transform: scale(0.96);
            }

            .org-intelligence-save-button {
              height: 40px;
              border: 1px solid rgba(86, 141, 237, 0.62);
              border-radius: 999px;
              background: var(--app-surface, rgba(86, 141, 237, 0.16));
              color: var(--app-text, #FFFFFF);
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              padding: 0 16px;
              font-family: Montserrat, sans-serif;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
              white-space: nowrap;
              transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease, opacity 0.18s ease;
            }

            .org-intelligence-save-button:hover:not(:disabled),
            .org-intelligence-save-button:focus-visible:not(:disabled) {
              background: var(--app-surface-hover, rgba(86, 141, 237, 0.26));
              border-color: rgba(86, 141, 237, 0.9);
              outline: none;
            }

            .org-intelligence-save-button:active:not(:disabled) {
              transform: scale(0.98);
            }

            .org-intelligence-save-button:disabled {
              opacity: 0.68;
              cursor: default;
            }

            .org-intelligence-configure-button {
              position: absolute;
              top: calc(100% + 10px);
              left: 50%;
              transform: translateX(-50%);
              border: 1px solid var(--node-color);
              border-radius: 20px;
              background: transparent;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 7px;
              padding: 5px 10px;
              font-family: Montserrat, sans-serif;
              font-size: 14px;
              font-weight: 600;
              line-height: 18px;
              cursor: pointer;
              white-space: nowrap;
              transition: background 0.18s ease, transform 0.18s ease, border-color 0.18s ease;
            }

            .org-intelligence-configure-button:hover,
            .org-intelligence-configure-button:focus-visible {
              background: var(--node-soft-color);
              transform: translateX(-50%) translateY(-1px);
              outline: none;
            }

            .org-intelligence-configure-button:active {
              transform: translateX(-50%) scale(0.98);
            }

            .org-intelligence-configure-button i {
              font-size: 12px;
              line-height: 1;
              transform: none !important;
            }

            [dir="rtl"] .org-intelligence-page-header,
            [dir="rtl"] .org-intelligence-configure-button {
              font-family: Alexandria, Montserrat, sans-serif !important;
            }

            @media (max-height: 760px) {
              .org-intelligence-configure-button {
                padding: 4px 9px;
                font-size: 12px;
              }
            }
          `}
        </style>
        <FeatureGate showBanner={false}>
          <main style={{ display: 'flex', width: '100%', height: '100%' }}>
            <section style={{ flex: activeNode ? '0 0 55%' : '1 1 100%', minWidth: 0, transition: 'flex-basis 240ms ease' }}>
              <OrgGraph activeNode={activeNode} onSelect={handleSelectNode} isRTL={isRTL} t={t} />
            </section>

            {activeNode && activeNodeConfig ? (
              <aside
                style={{
                  flex: '0 0 45%',
                  height: '100%',
                  minWidth: 0,
                  background: toRgba(COLORS.blue, 0.1),
                  padding: 40,
                  boxSizing: 'border-box',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  color: 'var(--app-text, #FFFFFF)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ margin: 0, color: 'var(--app-text, #FFFFFF)', fontFamily: 'Montserrat', fontSize: 24, fontWeight: 500 }}>
                      {activeNodeConfig.drawerTitle}
                    </h2>
                    {savedAtBySection[activeNode] ? (
                      <p style={{ margin: '6px 0 0', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.62)', fontFamily: 'Montserrat', fontSize: 12 }}>
                        {t('actions.savedAt', { time: formatSavedTime(savedAtBySection[activeNode]) })}
                      </p>
                    ) : null}
                  </div>
                  {editableNodeKeys.has(activeNode) && dirtyBySection[activeNode] ? (
                    <button
                      type="button"
                      className="org-intelligence-save-button"
                      onClick={saveActiveNode}
                      disabled={!companyUID || !!saving[activeNode]}
                    >
                      <i className={`fa-solid ${saving[activeNode] ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`} aria-hidden="true"></i>
                      <span>{saving[activeNode] ? uiText("Saving...") : uiText("Save Changes")}</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="org-intelligence-close"
                      onClick={() => setActiveNode(null)}
                      aria-label={uiText("Close drawer")}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                    </button>
                  )}
                </div>
                {renderDrawerContent()}
              </aside>
            ) : null}
          </main>
        </FeatureGate>
      </div>
    </div>
  );
};

export default OrgIntelligence;
